import ConditionalToolTip from "components/design/conditionalToolTip";
import { openDownloadUrl } from "util/openDownloadUrl";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";
import { Goto } from "components/icons/Goto";
import {
  FILE_FOLDER_DELETED,
  FILE_FOLDER_DELETE_FAILED,
  FILE_FOLDER_DOWNLOADED,
  FILE_FOLDER_DOWNLOAD_FAILED,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useDocPreview } from "hooks/useDocPreview";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useContext, useState } from "react";
import {
  useAddToRecycleBinMutation,
  useExportFolderMutation,
  useLazyGetSearchContentQuery,
} from "store/apis/documents";
import { getFileDownloadUrl } from "../../apis/documents";
import {
  COPY_LINK,
  DELETE_ACTION,
  DOWNLOAD_ACTION,
  FILE_DELETE_MESSAGE,
  FOLDER_DELETE_MESSAGE,
  FOLDER_TYPE,
  GO_TO_FOLDER_ACTION,
  ICON_DEFAULT_COLOR,
  MOVE_ACTION,
  MOVE_TO_RECYCLE_BIN,
  PREVIEW_ACTION,
  RENAME_ACTION,
} from "../../constants/documents";
import { getFileFolderShareLink } from "util/getFileFolderShareLink";
import { DeleteIcon } from "../icons/delete";
import { DownloadIcon } from "../icons/Download";
import { Link } from "../icons/Link";
import { MoveFile } from "../icons/moveFile";
import { OptionDropdown } from "../icons/optionDropdown";
import { PreviewIcon } from "../icons/Preview";
import { RenameIcon } from "../icons/rename";
import { DocumentContext } from "./documentContext";
import { RenameFileFolderModal } from "./documentsModal/renameFileFolderModal";
import {
  useRenameFileFolderMutation,
  useDeleteFileFolderMutation,
} from "store/apis/documents";
import { MoveDocModal } from "./documentsModal/MoveDocModal";
import { useModal } from "hooks/useModal";
import { Button } from "components/DesignSystem/Button/Button";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { ConfirmMoveToRecycleBin } from "./ConfirmMoveToRecycleBin";

const FileFolderActionDropDown = ({
  userGroupId,
  folderId,
  groupId,
  setLoad,
  folderName,
  setNewFolderName,
  itemType,
  isPreviewable,
  setMovedFolderName,
  groupDocID,
  ParentId,
  parentUuid,
  isLocked,
  listView,
  fileType,
  isPrivate,
}) => {
  const { trackEvent } = useAnalytics();
  const { alertToast, successToast } = useToast();
  const { setCurrentPageNo, setEntityFileAndFolder, handleFolderClick } =
    useContext(DocumentContext);
  const query = useQuery();
  const openPreview = useDocPreview();
  const { isAdmin } = useRoleBasedView();
  const searchTerm = query.get("search");
  const [renameModalShow, setRenameModalShow] = useState(false);
  const moveFileModal = useModal();
  const deleteModal = useModal();
  const moveToRecycleBin = useModal();
  const [getSearchContent] = useLazyGetSearchContentQuery();
  const [exportFolder, { reset }] = useExportFolderMutation();
  const { copyToClipboard } = useCopyToClipboard();
  const [renameFileAndFolder] = useRenameFileFolderMutation();
  const [deleteFileAndFolder, { isLoading: isDeleting }] =
    useDeleteFileFolderMutation();
  const [addContentToBin, { isLoading: isMovingToBin }] =
    useAddToRecycleBinMutation();

  // here folderId is the current item (file or folder) id
  const linkText = getFileFolderShareLink({
    groupId,
    itemId: folderId,
    itemType,
  });

  const openRenameModal = () => setRenameModalShow(true);
  const closeRenameModal = () => setRenameModalShow(false);

  const deleteFolder = async () => {
    try {
      await deleteFileAndFolder({
        groupId,
        folderId,
      }).unwrap();
      trackEvent(FILE_FOLDER_DELETED, {
        file_or_folder_ids: [folderId],
      });
      const toastTitle = "Deleted successfully";
      if (itemType === FOLDER_TYPE) {
        successToast({
          title: toastTitle,
          message: FOLDER_DELETE_MESSAGE,
        });
      } else {
        successToast({
          title: toastTitle,
          message: FILE_DELETE_MESSAGE,
        });
      }

      if (searchTerm) {
        const {
          files = [],
          folders = [],
          current_page,
        } = await getSearchContent({
          searchTerm,
          groupId,
          pageNum: 1,
        }).unwrap();
        setCurrentPageNo(current_page);

        let updatedMapData = [...folders, ...files].map((ele) => {
          return {
            ...ele,
            isChecked: false,
          };
        });
        setEntityFileAndFolder(updatedMapData);
      } else {
        setLoad((prev) => !prev);
      }
    } catch (error) {
      trackEvent(FILE_FOLDER_DELETE_FAILED, {
        file_or_folder_ids: folderId,
        error: error?.response?.data?.error?.message,
      });
      alertToast({ message: error?.response?.data?.error?.message });
    } finally {
      deleteModal.close();
    }
  };

  const handleMoveToRecycleBin = async () => {
    try {
      await addContentToBin({
        groupId,
        files: [folderId],
      }).unwrap();

      setEntityFileAndFolder((prev) =>
        prev.filter((file) => file.uuid !== folderId)
      );
      moveToRecycleBin.close();
      successToast({
        title: "Moved to recycle bin",
        message: `The file has been moved to recycle bin`,
      });
    } catch (error) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const renameFolder = async ({ file_name }) => {
    try {
      const data = await renameFileAndFolder({
        groupId,
        payload: { file_name },
        fileId: folderId,
      }).unwrap();
      successToast({ message: `Renamed to ${data.name}` });
      setNewFolderName(data.name);
      setLoad((prev) => !prev);
      closeRenameModal();
    } catch (error) {
      alertToast({ message: error?.response?.data?.error?.message });
    }
  };

  const openPreviewAndDownloadLink = async (preview) => {
    if (preview) {
      openPreview(folderId);
    } else {
      try {
        if (itemType === FOLDER_TYPE) {
          const { download_url } = await exportFolder({
            groupId,
            fileId: [folderId],
          }).unwrap();
          openDownloadUrl(download_url);
          reset();
        } else {
          const response = await getFileDownloadUrl({
            groupId,
            fileId: folderId,
          });
          openDownloadUrl(response.data.data.download_url);
        }
        trackEvent(FILE_FOLDER_DOWNLOADED, { file_or_folder_ids: [folderId] });
      } catch (error) {
        trackEvent(FILE_FOLDER_DOWNLOAD_FAILED, {
          file_or_folder_ids: [folderId],
          error: error?.response?.data?.error?.message,
        });

        alertToast({ message: error?.response?.data?.error?.message });
      }
    }
  };

  const handleActionType = (action) => {
    switch (action) {
      case DELETE_ACTION:
        deleteModal.open();
        break;
      case MOVE_TO_RECYCLE_BIN:
        moveToRecycleBin.open();
        break;
      case RENAME_ACTION:
        openRenameModal();
        break;
      case PREVIEW_ACTION:
        openPreviewAndDownloadLink(true);
        break;
      case DOWNLOAD_ACTION:
        openPreviewAndDownloadLink();
        break;
      case MOVE_ACTION:
        moveFileModal.open();
        break;
      case COPY_LINK:
        copyToClipboard(linkText);
        break;
      case GO_TO_FOLDER_ACTION:
        handleFolderClick(parentUuid);
        break;
      default:
        return null;
    }
  };

  const location = searchTerm
    ? [
        {
          Icon: Goto,
          title: "Go to folder",
          id: 7,
        },
      ]
    : [];

  // Hide delete for locked folders; admins can still delete user-created folders
  const folderDelete =
    isAdmin && !isLocked
      ? [
          {
            Icon: DeleteIcon,
            title: "Delete",
            id: 4,
          },
        ]
      : [];

  // Hide rename for locked folders
  const folderRename = !isLocked
    ? [
        {
          Icon: RenameIcon,
          title: "Rename",
          id: 1,
        },
      ]
    : [];

  const isPrev = isPreviewable
    ? [
        {
          Icon: PreviewIcon,
          title: "Preview",
          id: 1,
        },
      ]
    : [];

  let folderDropdownData;
  if (itemType === FOLDER_TYPE) {
    folderDropdownData = [
      ...folderRename,
      {
        Icon: Link,
        title: "Copy link",
        id: 2,
      },
      {
        Icon: DownloadIcon,
        title: "Download",
        id: 3,
      },
      ...location,
      ...folderDelete,
    ];
  } else {
    folderDropdownData = [
      ...isPrev,
      {
        Icon: DownloadIcon,
        title: "Download",
        id: 2,
      },
      {
        Icon: RenameIcon,
        title: "Rename",
        id: 4,
        isLocked,
      },
      {
        Icon: MoveFile,
        title: "Move to",
        id: 5,
        isLocked: false,
      },
      {
        Icon: Link,
        title: "Copy link",
        id: 6,
      },
      ...location,
      {
        Icon: DeleteIcon,
        title: isAdmin ? MOVE_TO_RECYCLE_BIN : "Delete",
        id: 3,
        isLocked,
      },
    ];
  }

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <DropDown.Root>
        <DropDown.Trigger asChild>
          <div className="t-size-6 t-rounded hover:t-bg-purple-20 data-state-open:t-bg-purple-20 t-items-center t-justify-center t-flex t-text-text-30">
            <OptionDropdown />
          </div>
        </DropDown.Trigger>
        <DropDown.Portal>
          <DropDown.Content sideOffset={8} align={listView ? "end" : "start"}>
            {folderDropdownData.map(({ id, Icon, title, isLocked }) => (
              <ConditionalToolTip
                condition={
                  isLocked &&
                  !isAdmin && (
                    <div className="t-max-w-32 t-text-center">
                      Please raise a request in Chat to make a change
                    </div>
                  )
                }
                placement={listView ? "left" : "right"}
                key={id}
              >
                <span>
                  <DropDown.Item
                    className="t-flex t-items-center t-gap-2 hover:t-text-purple data-[disabled]:t-text-neutral-30"
                    disabled={isLocked}
                    onSelect={() => handleActionType(title)}
                  >
                    <Icon color="currentColor" />
                    {title}
                  </DropDown.Item>
                </span>
              </ConditionalToolTip>
            ))}
          </DropDown.Content>
        </DropDown.Portal>
      </DropDown.Root>

      {renameModalShow && (
        <RenameFileFolderModal
          renameModalShow={renameModalShow}
          closeRenameModal={closeRenameModal}
          itemType={itemType}
          folderName={folderName}
          renameFolder={renameFolder}
        />
      )}

      {deleteModal.isOpen && (
        <DeleteConfirmationModal
          {...deleteModal}
          isLoading={isDeleting}
          confirm={deleteFolder}
          type="SINGLE"
        />
      )}

      {moveToRecycleBin.isOpen && (
        <ConfirmMoveToRecycleBin
          {...moveToRecycleBin}
          isLoading={isMovingToBin}
          confirm={handleMoveToRecycleBin}
        />
      )}

      <MoveDocModal
        isOpen={moveFileModal.isOpen}
        close={moveFileModal.close}
        itemIds={[folderId]}
        onMoveSuccess={() => {
          setLoad((prev) => !prev);
        }}
        containsPrivateDocuments={isPrivate}
      />
    </span>
  );
};

export default FileFolderActionDropDown;
